import { useSubmission } from 'api/submission';
import { APPLICATION_STEPS, QUOTE_FLOW_SECTIONS } from 'common/constants';
import { LAUNCH_DARKLY_FLAGS } from 'common/launchDarklyFlags';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { isEmpty } from 'lodash-es';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isStepEligible } from 'store/features/quoteStepper/quoteStepper.utils';
import { useAppSelector } from 'store/hooks';

/**
 * Custom hook for managing stepper navigation and URL synchronization.
 *
 * This hook handles:
 * 1. URL and stepper state synchronization
 * 2. Step eligibility validation
 * 3. Page title updates
 * 4. Initial load behavior (forcing step 0 for draft submissions)
 * 5. Scroll to top on navigation
 * 6. Special submission status handling
 * 7. URL cleanup for special statuses
 *
 * The hook ensures that:
 * - URL parameters always reflect the current stepper state
 * - Users can only navigate to eligible steps
 * - Browser history is properly maintained
 * - Page title is updated to reflect current section/step
 * - Page scrolls to top on navigation
 * - Draft submissions always start from section 0, step 0
 * - Quoted/accepted submissions stay in section 1
 * - Special statuses (expired, discarded, etc.) have section/step params removed
 * - Navigation is blocked for submissions without email
 * - Invalid step/section numbers are handled gracefully
 * - Step/section changes are synchronized with browser history
 */
export const useStepperNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: submissionId } = useParams() as { id: string };
  const [searchParams] = useSearchParams();
  const isHandlingURLChange = useRef(false);
  const isInitialLoad = useRef(true);
  const { data: submission, isLoading: isSubmissionLoading } = useSubmission(submissionId);

  const isVehicleStepDisabledForNY = useFeatureFlags([
    {
      flagKey: LAUNCH_DARKLY_FLAGS['disable-vehicle-for-ny'],
      defaultValue: true,
    },
  ])[0] as boolean;

  const stepsSkipConditions = useMemo(
    () => ({
      [APPLICATION_STEPS.vehicles.key]: submission?.us_state === 'NY' && isVehicleStepDisabledForNY,
    }),
    [submission?.us_state, isVehicleStepDisabledForNY],
  );

  const {
    currentSection,
    currentStep,
    actions: { setSection, setCurrentStep },
  } = useAppSelector('quoteStepper', ['currentSection', 'currentStep', 'actions']);

  // Helper function to handle navigation to section 0, step 0
  const navigateToStart = () => {
    isHandlingURLChange.current = true;
    navigate(
      `?${new URLSearchParams({
        ...Object.fromEntries(searchParams),
        section: '0',
        step: '0',
      })}`,
      { replace: true },
    );
    isHandlingURLChange.current = false;
    isInitialLoad.current = false;
  };

  /**
   * These statuses are special and we don't want to update the URL for them
   * We want to clean up `step` and `section` params for them
   *
   * These are handling in a different way in the `SubmissionRenderer` component
   *
   * We also don't want to update the URL if `email` is not provided yet
   */
  const isSpecialStatus =
    (submission?.status &&
      [
        'expired',
        'discarded',
        'issued',
        'declined',
        'referred',
        'failed_to_quote',
        'quote_pending',
        'not_eligible',
      ].includes(submission.status)) ||
    (submission && isEmpty(submission?.email));

  // Clean up search params for special submission statuses
  useLayoutEffect(() => {
    if (isSubmissionLoading) return;

    // Don't update URL if we're handling a URL change
    if (isHandlingURLChange.current) {
      return;
    }

    if (isSpecialStatus) {
      const params = new URLSearchParams(searchParams);
      const hasNavigationParams = params.has('section') || params.has('step');

      if (hasNavigationParams) {
        params.delete('section');
        params.delete('step');
        const newSearch = params.toString();
        navigate(newSearch ? `?${newSearch}` : location.pathname, { replace: true });
      }
    }
  }, [submission?.status, isSubmissionLoading]);

  // Update page title based on URL parameters
  useLayoutEffect(() => {
    const sectionParam = searchParams.get('section');
    const stepParam = searchParams.get('step');

    if (sectionParam !== null) {
      const targetSection = parseInt(sectionParam, 10);
      const section = QUOTE_FLOW_SECTIONS[targetSection];
      if (!section) return;

      let title = `THREE Insurance - ${section.label}`;

      if (stepParam !== null) {
        const targetStep = parseInt(stepParam, 10);
        const stepData = section.steps?.[targetStep];
        if (stepData) {
          title += ` - ${stepData.label}`;
        }
      }

      document.title = title;
    }

    // Scroll to top with a small delay to ensure content has updated
    setTimeout(() => {
      window?.scrollTo(0, 0);
    }, 1);
  }, [searchParams]);

  // Update URL when stepper state changes
  useLayoutEffect(() => {
    // Wait for submission data to be loaded
    if (isSubmissionLoading) return;

    // Don't update URL if we're handling a URL change
    if (isHandlingURLChange.current) {
      return;
    }

    // Don't update URL if the submission status is one of the special statuses
    if (isSpecialStatus) {
      return;
    }

    const params = new URLSearchParams(searchParams);
    const currentSectionParam = params.get('section');
    const currentStepParam = params.get('step');

    // Only update URL if the params are different from current state
    if (
      currentSectionParam !== currentSection.toString() ||
      currentStepParam !== currentStep.toString()
    ) {
      navigate(
        `?${new URLSearchParams({
          ...Object.fromEntries(searchParams),
          section: currentSection.toString(),
          step: currentStep.toString(),
        })}`,
        {
          state: {
            from: {
              section: currentSectionParam,
              step: currentStepParam,
              path: location.pathname + location.search,
            },
          },
        },
      );
    }
  }, [currentSection, currentStep]);

  // Handle URL changes (including initial load and browser navigation)
  // eslint-disable-next-line complexity
  useLayoutEffect(() => {
    // Wait for submission data to be loaded
    if (isSubmissionLoading) return;

    // Don't update URL if the submission status is one of the special statuses
    if (isSpecialStatus) return;

    const sectionParam = searchParams.get('section');
    const stepParam = searchParams.get('step');

    const shouldResetToStart = isInitialLoad.current && submission?.status === 'draft';

    // If this was initial load for a draft submission, update the URL to section 0, step 0
    if (shouldResetToStart) {
      navigateToStart();
      return;
    }

    if (sectionParam !== null && stepParam !== null) {
      let targetSection = isInitialLoad.current ? 1 : parseInt(sectionParam, 10);
      const targetStep = parseInt(stepParam, 10);

      // If the submission is not a draft, we want to start from section 1
      if (targetSection === 0 && submission?.status !== 'draft') {
        targetSection = 1;
      }

      try {
        isHandlingURLChange.current = true;

        // Only update state if it's different from current state
        if (targetSection !== currentSection) {
          setSection(targetSection);
        }

        if (targetSection === 0 && targetStep !== currentStep) {
          const currentSectionSteps = QUOTE_FLOW_SECTIONS[targetSection].steps || [];
          const targetStepData = currentSectionSteps[targetStep];

          if (!targetStepData) {
            throw new Error('Invalid step');
          }

          // Check if the target step is eligible
          const isEligible = isStepEligible({
            submissionId,
            step: targetStepData,
            stepsSkipConditions,
          });

          if (isEligible) {
            setCurrentStep(targetStep);
          } else {
            // If step is not eligible, stay at current step
            throw new Error('Step not eligible');
          }
        }
      } catch (_) {
        // If step change fails (invalid step), try to go back to the previous valid state
        const previousState = location.state?.from;
        if (previousState?.section && previousState?.step) {
          navigate(previousState.path, { replace: true });
        } else {
          window.history.back();
        }
      } finally {
        isHandlingURLChange.current = false;
        isInitialLoad.current = false;
      }
    }
  }, [searchParams, submission?.status, isSubmissionLoading]);
};

export default useStepperNavigation;
